import { ModelType, type UnwrappedApiServiceModelDescriptor } from "@doitintl/cmp-models";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import { cloudflowTexts, globalText } from "../../../assets/texts";
import { GenericForm } from "../CloudflowBuilder/ApiActionParametersForm/ApiActionParametersForm";
import { generateApiActionParametersSchema } from "../CloudflowBuilder/ApiActionParametersForm/useApiActionParametersSchema";
import { type VariableFormSubmitData, type VariableFormValues, type VariableMode } from "../GlobalVariables/types";
import { type VariableScope, VariableType } from "../types";

interface CreateVariableModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: VariableFormSubmitData) => Promise<void>;
  scope: VariableScope;
  mode?: VariableMode;
  initialData?: VariableFormValues;
  onTransitionExited: () => void;
}

const getInputModelForType = (type: VariableType): UnwrappedApiServiceModelDescriptor => {
  switch (type) {
    case VariableType.INTEGER:
      return {
        type: ModelType.INTEGER,
      };
    case VariableType.FLOAT:
      return {
        type: ModelType.FLOAT,
      };
    case VariableType.BOOLEAN:
      return {
        type: ModelType.BOOLEAN,
      };
    case VariableType.STRING:
      return {
        type: ModelType.STRING,
        minLength: 1,
        maxLength: 256,
      };
    default:
      return {
        type: ModelType.STRING,
        minLength: 1,
        maxLength: 256,
      };
  }
};

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .matches(
      /^[a-zA-Z_][a-zA-Z0-9_]*$/,
      "Name must start with a letter or underscore and contain only letters, numbers, and underscores"
    ),
  type: Yup.string().oneOf(Object.values(VariableType)).required("Type is required"),
  value: Yup.mixed().when("type", ([selectedType]) => {
    const model = getInputModelForType(selectedType);
    return generateApiActionParametersSchema(model, true);
  }),
});

const title = {
  create: {
    global: cloudflowTexts.CREATE_GLOBAL_VARIABLE,
    local: cloudflowTexts.CREATE_LOCAL_VARIABLE,
  },
  edit: {
    global: cloudflowTexts.EDIT_GLOBAL_VARIABLE,
    local: cloudflowTexts.EDIT_LOCAL_VARIABLE,
  },
} as const;

export const CreateVariableModal = ({
  open,
  onClose,
  onSubmit,
  scope,
  mode = "create",
  initialData,
  onTransitionExited,
}: CreateVariableModalProps) => {
  const initialValues: VariableFormValues = initialData || {
    name: "",
    type: VariableType.STRING,
    value: null,
  };

  const { TYPE, VALUE, CANCEL } = cloudflowTexts;
  const { CREATE, NAME, SAVE } = globalText;

  return (
    <Dialog open={open} onClose={onClose} fullWidth onTransitionExited={onTransitionExited}>
      <DialogTitle>{title[mode][scope]}</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values: VariableFormValues) => {
          await onSubmit({
            name: values.name,
            type: values.type,
            value: values.value,
            mode,
          });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
          isValid,
          handleSubmit,
          dirty,
          setValues,
          validateForm,
        }) => (
          <Form onSubmit={handleSubmit}>
            <DialogContent sx={{ pt: 1 }}>
              <Stack direction="column" spacing={2}>
                <TextField
                  name="name"
                  label={NAME}
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && !!errors.name}
                  helperText={touched.name && errors.name}
                  fullWidth
                  required
                />
                <TextField
                  name="type"
                  label={TYPE}
                  value={values.type}
                  onChange={(e) => {
                    // TODO: check if this should be disabled for edit mode when already referenced in the node
                    const newType = e.target.value as VariableType;
                    setValues((prev) => ({
                      ...prev,
                      type: newType,
                      value: newType === VariableType.BOOLEAN ? false : null,
                    }));
                    validateForm();
                  }}
                  onBlur={handleBlur}
                  error={touched.type && !!errors.type}
                  select
                  fullWidth
                  size="small"
                  required
                >
                  {Object.values(VariableType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
                <GenericForm
                  inputModel={getInputModelForType(values.type)}
                  fieldPath="value"
                  label={VALUE}
                  rootPath="value"
                />
              </Stack>
            </DialogContent>
            <Divider />
            <DialogActions>
              <Button onClick={onClose}>{CANCEL}</Button>
              <Button type="submit" variant="contained" disabled={isSubmitting || !isValid || !dirty}>
                {mode === "create" ? CREATE : SAVE}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
